import React from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const HowItWorks: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      icon: UploadFileIcon,
      title: 'Upload Your Material',
      description: 'Upload your study materials in any format—PDFs, Word documents, handwritten notes, or photos. Our system supports multiple file types and automatically extracts text while preserving formatting.',
      color: '#3b82f6', // Blue
      stepNumber: '01',
    },
    {
      icon: PsychologyIcon,
      title: 'AI Processing with RAG',
      description: 'Our advanced AI uses Retrieval-Augmented Generation (RAG) technology to deeply analyze your content. It identifies key concepts, understands relationships between topics, and maps your knowledge structure.',
      color: '#8b5cf6', // Purple
      stepNumber: '02',
    },
    {
      icon: AutoAwesomeIcon,
      title: 'Instant Learning Tools',
      description: 'Watch as our AI instantly creates personalized learning tools from your materials. Generate practice quizzes that target your weak areas, create flashcards with spaced repetition, and develop interactive games.',
      color: '#60a5fa', // Light blue
      stepNumber: '03',
    },
    {
      icon: TrendingUpIcon,
      title: 'Track & Improve',
      description: 'Monitor your learning journey with comprehensive analytics and insights. Our AI tracks your progress, identifies patterns in your learning behavior, and suggests personalized study strategies.',
      color: '#a78bfa', // Light purple
      stepNumber: '04',
    },
  ];

  const learningFeatures = [
    {
      icon: SchoolIcon,
      title: 'Personalized Quizzes',
      description: 'AI-generated questions from your materials',
      color: '#3b82f6',
    },
    {
      icon: PsychologyAltIcon,
      title: 'Smart Flashcards',
      description: 'Spaced repetition with adaptive learning',
      color: '#8b5cf6',
    },
    {
      icon: GroupsIcon,
      title: 'Multiplayer Games',
      description: 'Real-time competitions with classmates',
      color: '#60a5fa',
    },
    {
      icon: EmojiEventsIcon,
      title: 'Rewards & Achievements',
      description: 'Earn tokens and unlock achievements',
      color: '#a78bfa',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const stepVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95,
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: 'spring' as const, 
        stiffness: 100, 
        damping: 15,
        duration: 0.6,
      } 
    },
  };

  const featureVariants = {
    hidden: { 
      scale: 0.8, 
      opacity: 0,
      y: 20,
    },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: 'spring' as const, 
        stiffness: 200, 
        damping: 20,
        duration: 0.5,
      } 
    },
  };

  return (
    <Box 
      component="section" 
      id="how-it-works" 
      sx={{ 
        py: { xs: 6, md: 12 }, 
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(0, 255, 136, 0.02) 0%, transparent 50%)
          `,
          zIndex: 0,
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            component="h2"
            sx={{
              fontWeight: 400,
              mb: { xs: 2, md: 2 },
              color: 'text.primary',
              textAlign: 'center',
              fontFamily: '"Charter", "Georgia", serif',
              px: { xs: 2, md: 0 },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            How Knoowl Works
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: { xs: 4, md: 8 },
              textAlign: 'center',
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.7,
              fontFamily: '"Kievit", "Roboto", sans-serif',
              px: { xs: 2, md: 0 },
              fontSize: { xs: '0.9rem', md: '1rem' },
            }}
          >
            Experience a seamless and intuitive learning journey with Knoowl's powerful AI. Our four-step process transforms your study materials into intelligent, personalized learning experiences.
          </Typography>

          {/* Process Pathway */}
          <Box
            sx={{
              position: 'relative',
              mb: { xs: 6, md: 8 },
            }}
          >
            {/* Steps */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                gap: { xs: 4, md: 3 },
                position: 'relative',
              }}
            >
              {steps.map((step, index) => (
                <motion.div key={index} variants={stepVariants}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      position: 'relative',
                    }}
                  >
                    {/* Pathway Dot (Desktop only) */}
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}80 100%)`,
                        border: `3px solid ${theme.palette.background.paper}`,
                        boxShadow: `0 0 15px ${step.color}40`,
                        zIndex: 2,
                        mb: { xs: 2, md: 3 },
                        display: { xs: 'none', md: 'block' },
                      }}
                    />

                    {/* Step Content */}
                    <Box
                      sx={{
                        width: '100%',
                        order: { xs: 2, md: 1 },
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 4, md: 5 },
                          pt: { xs: 5, md: 6 },
                          borderRadius: 3,
                          background: `linear-gradient(135deg, ${step.color}08 0%, ${step.color}12 100%)`,
                          border: `1px solid ${step.color}30`,
                          backdropFilter: 'blur(10px)',
                          position: 'relative',
                          overflow: 'visible',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: 'translateY(0)',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(135deg, ${step.color}05 0%, transparent 50%)`,
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                          },
                          '&:hover': {
                            transform: 'translateY(-12px) scale(1.02)',
                            boxShadow: `0 25px 50px ${step.color}25, 0 10px 20px rgba(0, 0, 0, 0.1)`,
                            borderColor: `${step.color}50`,
                            '&::before': {
                              opacity: 1,
                            },
                            '& .card-icon': {
                              transform: 'scale(1.15) rotate(5deg)',
                              boxShadow: `0 0 35px ${step.color}60`,
                            },
                            '& .card-title': {
                              transform: 'translateY(-2px)',
                              color: step.color,
                            },
                          },
                          '&:active': {
                            transform: 'translateY(-8px) scale(1.01)',
                            transition: 'all 0.1s ease',
                          },
                        }}
                      >
                        {/* Step Number */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -20,
                            left: 20,
                            width: 45,
                            height: 45,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}80 100%)`,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '1.2rem',
                            fontFamily: '"Kievit", "Roboto", sans-serif',
                            zIndex: 10,
                            boxShadow: `0 0 15px ${step.color}40`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              boxShadow: `0 0 25px ${step.color}60`,
                            },
                          }}
                        >
                          {step.stepNumber}
                        </Box>

                        {/* Icon */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            mt: 1,
                          }}
                        >
                          <Box
                            className="card-icon"
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: `linear-gradient(135deg, ${step.color}20 0%, ${step.color}40 100%)`,
                              color: step.color,
                              boxShadow: `0 0 20px ${step.color}30`,
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                boxShadow: `0 0 30px ${step.color}50`,
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <step.icon sx={{ fontSize: 32 }} />
                          </Box>
                          <Typography
                            className="card-title"
                            variant="h6"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              mb: { xs: 1.5, md: 2 },
                              fontFamily: '"Charter", "Georgia", serif',
                              fontSize: { xs: '1.1rem', md: '1.25rem' },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {step.title}
                          </Typography>
                        </Box>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            fontFamily: '"Kievit", "Roboto", sans-serif',
                            fontSize: { xs: '0.85rem', md: '0.875rem' },
                            flex: 1,
                            transition: 'color 0.3s ease',
                          }}
                        >
                          {step.description}
                        </Typography>
                      </Paper>
                    </Box>

                    {/* Arrow (Desktop only) */}
                    {!isMobile && index < steps.length - 1 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          right: '-20px',
                          transform: 'translateY(-50%)',
                          zIndex: 1,
                          color: step.color,
                          filter: `drop-shadow(0 0 5px ${step.color}40)`,
                        }}
                      >
                        <ArrowForwardIcon sx={{ fontSize: 24 }} />
                      </Box>
                    )}
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>

          {/* Enhanced Learning Journey Visualization */}
          <Box
            sx={{
              mt: { xs: 8, md: 10 },
              p: { xs: 3, md: 6 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 50%, ${theme.palette.neon?.main}08 100%)`,
              border: `1px solid ${theme.palette.primary.main}20`,
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              mx: { xs: 2, md: 0 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}05 0%, transparent 50%)`,
                opacity: 0,
                transition: 'opacity 0.3s ease',
              },
              '&:hover::before': {
                opacity: 1,
              },
            }}
          >
            {/* Background decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '10%',
                width: { xs: 60, md: 100 },
                height: { xs: 60, md: 100 },
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
                filter: 'blur(20px)',
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '20%',
                left: '5%',
                width: { xs: 50, md: 80 },
                height: { xs: 50, md: 80 },
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.primary.main}15 100%)`,
                filter: 'blur(15px)',
                zIndex: 0,
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant={isMobile ? 'h5' : 'h3'}
                sx={{
                  fontWeight: 400,
                  mb: { xs: 2, md: 2 },
                  color: 'text.primary',
                  textAlign: 'center',
                  fontFamily: '"Charter", "Georgia", serif',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Your Learning Journey Unfolded
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: { xs: 3, md: 4 },
                  textAlign: 'center',
                  maxWidth: '800px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  fontFamily: '"Kievit", "Roboto", sans-serif',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                Watch as your study materials transform into an intelligent learning ecosystem. From simple uploads to complex knowledge mastery, every step is powered by cutting-edge AI technology.
              </Typography>

              {/* Learning Features Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: { xs: 2, md: 3 },
                  mb: { xs: 3, md: 4 },
                }}
              >
                {learningFeatures.map((feature, index) => (
                  <motion.div key={index} variants={featureVariants}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        p: { xs: 1.5, md: 2 },
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          background: 'rgba(255, 255, 255, 0.08)',
                          boxShadow: `0 10px 25px ${feature.color}20`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 50, md: 60 },
                          height: { xs: 50, md: 60 },
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: { xs: 1, md: 2 },
                          background: `linear-gradient(135deg, ${feature.color}25 0%, ${feature.color}40 100%)`,
                          color: feature.color,
                          boxShadow: `0 0 15px ${feature.color}30`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: `0 0 25px ${feature.color}50`,
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <feature.icon sx={{ fontSize: { xs: 24, md: 28 } }} />
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: { xs: 0.5, md: 1 },
                          fontFamily: '"Kievit", "Roboto", sans-serif',
                          fontSize: { xs: '0.8rem', md: '0.875rem' },
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.4,
                          fontFamily: '"Kievit", "Roboto", sans-serif',
                          fontSize: { xs: '0.7rem', md: '0.75rem' },
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>

              {/* Key Benefits */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    mb: { xs: 2, md: 3 },
                    color: 'text.primary',
                    fontFamily: '"Charter", "Georgia", serif',
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}
                >
                  Why Students Choose Knoowl
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1, md: 2 }}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{ gap: { xs: 1, md: 2 } }}
                >
                  <Chip
                    label="90% Faster Learning"
                    sx={{
                      bgcolor: `${theme.palette.primary.main}20`,
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      fontFamily: '"Kievit", "Roboto", sans-serif',
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      border: `1px solid ${theme.palette.primary.main}30`,
                      '&:hover': {
                        bgcolor: `${theme.palette.primary.main}30`,
                        boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
                      },
                    }}
                  />
                  <Chip
                    label="Personalized AI Tutor"
                    sx={{
                      bgcolor: `${theme.palette.secondary.main}20`,
                      color: theme.palette.secondary.main,
                      fontWeight: 500,
                      fontFamily: '"Kievit", "Roboto", sans-serif',
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      border: `1px solid ${theme.palette.secondary.main}30`,
                      '&:hover': {
                        bgcolor: `${theme.palette.secondary.main}30`,
                        boxShadow: `0 0 10px ${theme.palette.secondary.main}30`,
                      },
                    }}
                  />
                  <Chip
                    label="Blockchain Rewards"
                    sx={{
                      bgcolor: `${theme.palette.primary.main}20`,
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      fontFamily: '"Kievit", "Roboto", sans-serif',
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      border: `1px solid ${theme.palette.primary.main}30`,
                      '&:hover': {
                        bgcolor: `${theme.palette.primary.main}30`,
                        boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
                      },
                    }}
                  />
                  <Chip
                    label="Multiplayer Learning"
                    sx={{
                      bgcolor: `${theme.palette.secondary.main}20`,
                      color: theme.palette.secondary.main,
                      fontWeight: 500,
                      fontFamily: '"Kievit", "Roboto", sans-serif',
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      border: `1px solid ${theme.palette.secondary.main}30`,
                      '&:hover': {
                        bgcolor: `${theme.palette.secondary.main}30`,
                        boxShadow: `0 0 10px ${theme.palette.secondary.main}30`,
                      },
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HowItWorks; 